import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { BREAKPOINT, BreakpointService, CurrentProductService } from '@spartacus/storefront';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { concatMap, distinctUntilChanged, mergeMap, switchMap, take } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { ShimanoProduct } from 'src/app/model/product.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { PDP_PRICE, TooltipIconService } from 'src/app/service/tooltip-icon/tooltip-icon.service';
import { DealerPriceCheckerService } from 'src/app/utility/dealer-price-checker.service';

@Component({
  selector: 'app-product-pricing',
  templateUrl: './product-pricing.component.html',
  styleUrls: ['./product-pricing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ProductPricingComponent implements OnInit, OnDestroy {
  product$: Observable<ShimanoProduct> = this.currentProductService.getProduct().pipe(
    distinctUntilChanged(),
    concatMap((currentProduct: any) => {
      let dealerPrices$ = of(null);
      if (currentProduct && currentProduct.code) {
        dealerPrices$ = this.getDealerPrices([currentProduct.code]);
        this.erpPromotions$ = dealerPrices$;
      }
      return forkJoin([of(currentProduct), dealerPrices$]);
    }),
    mergeMap(([currentProduct, dealerPricesData]: [any, any]) => {
      let currentProductDealerPrice: number;
      if (dealerPricesData) {
        this.dealerPrices = dealerPricesData.dealerPrices;
        currentProductDealerPrice = this.dealerPriceValue(currentProduct.code);
        this.componentService.setPdpDealerPrice(currentProductDealerPrice);
      }
      return of(currentProduct);
    })
  ) as Observable<ShimanoProduct>;
  showSrp = false;
  srpSubscription$: Subscription;
  productSubscription$: Subscription;
  productCodesList: string[] = [];
  dealerPrices: DealerPrice[];
  dealerPrices$: Observable<any>;
  erpPromotions$: Observable<any>;
  pos: any;
  isMobile = false;
  isMobileLandscape = false;
  isTablet = false;
  csDesktopWidth = this.breakpointService.getSize(BREAKPOINT.lg);
  csTabletWidth = this.breakpointService.getSize(BREAKPOINT.sm);
  hasDealerPrice = true;
  dealerPriceCheckerSubscription$: Subscription;
  basepriceBehaviorConfig$: Subscription;
  basepriceHidden = false;

  tooltipIconConfigSubscription$: Subscription;
  showGstInclusiveSubscription$: Subscription;
  showGstExclusiveSubscription$: Subscription;

  tooltipIconConfig: any;
  gstInclusive: boolean = false;
  gstExclusive: boolean = false;

  @HostBinding('class.float') float: boolean;
  @HostBinding('class.gst-labels-on') gstLabelsOn: boolean;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isMobile = window.innerWidth < this.csTabletWidth;
    this.isTablet = window.innerWidth < this.csDesktopWidth && window.innerWidth >= this.csTabletWidth;
    this.isMobileLandscape = window.innerWidth > 640 && window.innerWidth < 768;

    let heightCut = 360;
    if (this.isMobile) {
      heightCut = 357 + 68;
    }
    if (this.isMobileLandscape) {
      heightCut = 400 + 357 - 68;
    }

    this.pos = this.el.nativeElement.getBoundingClientRect().top + heightCut;

    if (this.pos && this.pos > 300) {
      this.float = window.scrollY > this.pos;
      this.componentService.setIsFloat(this.float);
    }
    this.cdr.markForCheck();
  }

  constructor(
    protected currentProductService: CurrentProductService,
    private componentService: ComponentService,
    private dealerPriceService: DealerPriceService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private breakpointService: BreakpointService,
    private dealerPriceChecker: DealerPriceCheckerService,
    private countryService: ShimanoCountryService,
    private languageService: ShimanoLanguageService,
    private tooltipIconService: TooltipIconService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.dealerPriceCheckerSubscription$ = this.componentService.hasDealerPrice$.subscribe((hasDealerPrice) => {
      this.hasDealerPrice = hasDealerPrice;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });

    this.basepriceBehaviorConfig$ = this.dealerPriceService.getBasepriceBehavior$().subscribe((hideBaseprice: any) => {
      if (hideBaseprice === 'true') {
        this.basepriceHidden = true;
      } else {
        this.basepriceHidden = false;
      }
      this.cdr.detectChanges();
    });
    this.tooltipIconConfigSubscription$ = this.activeCountry$
      .pipe(
        take(1),
        switchMap((activeCountry) => this.tooltipIconService.getTooltipIconConfig(activeCountry, PDP_PRICE))
      )
      .subscribe((tooltipIconConfig) => {
        if (tooltipIconConfig) {
          this.tooltipIconConfig = tooltipIconConfig;
        }
        this.cdr.detectChanges();
      });

    this.showGstInclusiveSubscription$ = this.activeCountry$
      .pipe(
        take(1),
        switchMap((activeCountry) =>
          this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_INCLUSIVE)
        )
      )
      .subscribe((showGstInclusive) => {
        if (showGstInclusive) {
          this.gstInclusive = true;
        } else {
          this.gstInclusive = false;
        }
        this.cdr.detectChanges();
      });

    this.showGstExclusiveSubscription$ = this.activeCountry$
      .pipe(
        take(1),
        switchMap((activeCountry) =>
          this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_EXCLUSIVE)
        )
      )
      .subscribe((showGstExclusive) => {
        if (showGstExclusive) {
          this.gstExclusive = true;
        } else {
          this.gstExclusive = false;
        }

        if (
          showGstExclusive &&
          this.float &&
          this.dealerPrices[0] &&
          this.dealerPrices[0].hasOwnProperty('promotionMessage') &&
          this.dealerPrices[0].promotionMessage.length
        ) {
          this.gstLabelsOn = true;
          this.componentService.gstLabelsOn$.next(true);
        } else {
          this.gstLabelsOn = false;
          this.componentService.gstLabelsOn$.next(false);
        }

        this.cdr.detectChanges();
      });
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  getDealerPrices(productCodes: string[]): Observable<any> {
    return this.dealerPriceService.getDealerPricesByProductCodes(productCodes);
  }

  dealerPrice(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode);
    }
  }

  dealerPriceValue(productCode: string): number {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceValueByProductCode(this.dealerPrices, productCode);
    }
  }

  setEnabled() {
    this.componentService.setIsAddToCartDisabled(false);
  }
  setDisabled() {
    this.componentService.setIsAddToCartDisabled(true);
  }

  get activeCountry$(): Observable<string> {
    return this.countryService.getActive();
  }

  get activeLanguage$(): Observable<string> {
    return this.languageService.getActive();
  }

  ngOnDestroy() {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }

    if (this.productSubscription$) {
      this.productSubscription$.unsubscribe();
    }
    if (this.dealerPriceCheckerSubscription$) {
      this.dealerPriceCheckerSubscription$.unsubscribe();
    }
    if (this.basepriceBehaviorConfig$) {
      this.basepriceBehaviorConfig$.unsubscribe();
    }

    if (this.showGstInclusiveSubscription$) {
      this.showGstInclusiveSubscription$.unsubscribe();
    }

    if (this.showGstExclusiveSubscription$) {
      this.showGstExclusiveSubscription$.unsubscribe();
    }

    if (this.tooltipIconConfigSubscription$) {
      this.tooltipIconConfigSubscription$.unsubscribe();
    }
  }
}
